import { AbacoWebModule, RouterReadyEvent, VueRouter, MenuItem } from '@abaco/web-common/src/app';
import globals from "./globals";
import Vue from "vue";
import myRoutes from "./routes";
import MainStateModule from './state';
import { HTTPClient } from '@abaco/web-common/src/HTTP';
import { createMenu } from './menu';
import DamageClaimApi from './api/DamageClaimApi';
import FarmRegistry from '@abaco/farm-registry/src/modules/farm-registry';
import GlobalState from '@abaco/portal-global-state/src/modules/global-state';
import type {
    DamageClaimEvent,
    DamageClaimRecord
} from "./models/DamageClaimModel";
import type {
    CertigrafPrecertificateData,
} from "./models/CertigrafModel";

const state = new MainStateModule();

export const MODULE_ID = "dclaim_module";

export default class DamageClaimModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;

    private innerApi!: DamageClaimApi;
    private apiCreated = false;

    private router!: VueRouter;

    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly globalComponents = globals;
    readonly vuexModule = state;

    private evnBus = new Vue();

    get eventBus() {
        return this.evnBus;
    }

    menu: MenuItem[] = [];

    dependsOn = {
        [FarmRegistry.MODULE_ID]: () => new FarmRegistry(),
        [GlobalState.MODULE_ID]: () => new GlobalState()
    }

    onRouterReady(evt: RouterReadyEvent) {
        this.router = evt.router;
        this.menu = createMenu(this.router);
    }

    async beforeEnterAnyRoutes(http: HTTPClient) {
        if (this.apiCreated) {
            return;
        }
        this.apiCreated = true;
        const DamageClaimApi = (await import("./api/DamageClaimApi")).default;
        this.innerApi = new DamageClaimApi(http);
    }

    public get api(){
        return this.innerApi;
    }


    /**
     * MUTATION
     * @param id
     * @param data
     */
    public addDamageClaimRecord(id: number, data: DamageClaimRecord){
        Vue.set(this.vuexModule, 'dclaimRecords', {
            ...this.vuexModule.dclaimRecords,
            [id]: data
        });
    }

    /**
     * MUTATION
     * @param id
     * @param data
     */
    public editDamageClaimRecord(id: number, data: DamageClaimRecord){
        if(!this.vuexModule.dclaimRecords[id])
            return;
        Vue.set(this.vuexModule, 'dclaimRecords', {
            ...this.vuexModule.dclaimRecords,
            [id]: {
                ...this.vuexModule.dclaimRecords[id],
                ...data,
            }
        });
    }

    /**
     * MUTATION
     * @param id
     */
    public deleteDamageClaimRecord(id: number){
        const temp = { ...this.vuexModule.dclaimRecords };
        delete temp[id];
        Vue.set(this.vuexModule, 'dclaimRecords', temp);
    }

    // /**
    //  * MUTATION
    //  * @param id
    //  */
    // public deleteCertigrafPlot(id: number){
    //     const temp = { ...this.vuexModule.plotRecords };
    //     delete temp[id];
    //     Vue.set(this.vuexModule, 'plotRecords', temp);
    // }

    /**
     * MUTATION
     *
     * @param id
     * @param toggle - default: toggle current value
     */
    public toggleCertigrafPlot(id: number, toggle: null | number = null){
        if(!this.vuexModule.plotRecords[id])
            return;
        const fl_valid = toggle ? toggle : this.vuexModule.plotRecords[id].fl_valid ? 0 : 1;
        // change value in state
        Vue.set(this.vuexModule, 'plotRecords', {
            ...this.vuexModule.plotRecords,
            [id]: {
                ...this.vuexModule.plotRecords[id],
                fl_valid,
            }
        });
    }

    /**
     * MUTATION
     *
     * @param id
     * @param data
     */
    public addPrecertificateDetailsRecord(id: number, data: CertigrafPrecertificateData){
        Vue.set(this.vuexModule, 'pcertDetailsRecords', {
            ...this.vuexModule.pcertDetailsRecords,
            [id]: data
        });
        // this.userData = {
        //     name: data.pcertFirm?.first_name || '',
        //     surname: data.pcertFirm?.last_name || '',
        //     telephone: data.pcertFirm?.phone || '',
        //     email: data.pcertFirm?.email || ''
        // }
    }

    /**
     * MUTATION
     * @param id
     * @param data
     */
    public editPrecertificateDetailsRecord(id: number, data: CertigrafPrecertificateData){
        if(!this.vuexModule.pcertDetailsRecords[id])
            return;
        Vue.set(this.vuexModule, 'pcertDetailsRecords', {
            ...this.vuexModule.pcertDetailsRecords,
            [id]: {
                ...this.vuexModule.pcertDetailsRecords[id],
                ...data,
            }
        });
    }

    /**
     * MUTATION
     *
     * @param id
     * @param data
     */
    public addDamageClaimEventRecord(id: number, data: DamageClaimEvent){
        Vue.set(this.vuexModule, 'dclaimEventRecords', {
            ...this.vuexModule.dclaimEventRecords,
            [id]: data
        });
    }

    /**
     * MUTATION
     * Reset all the inserted data
     */
    public async resetForms() {
        return new Promise((resolve) => {
            if(this.vuexModule.formMode === 0) {
                this.vuexModule.curStep = 1;
            }
            this.vuexModule.dclaimPhotos = [];
            this.vuexModule.plotRecords = {};
            this.vuexModule.pcertRecords = {};
            this.vuexModule.selectedProduct = null;
            this.vuexModule.selectedDclaim = null;
            resolve();
        });
    }

    /**
     * GETTER
     */
    public get precertificateData() {
        if(!this.vuexModule.selectedDclaim)
            return;
        return this.vuexModule.pcertRecords[this.vuexModule.dclaimRecords[this.vuexModule.selectedDclaim].pcert_id];
    }

    /**
     * GETTER
     */
    public get precertificateDetails() {
        if(!this.vuexModule.selectedDclaim)
            return;
        return this.vuexModule.pcertDetailsRecords[this.vuexModule.dclaimRecords[this.vuexModule.selectedDclaim].pcert_id];
    }

    /**
     * GETTER
     * Some intermediaries could be listed as condifesa
     */
    public get managerSubjects(){
        if(!this.vuexModule.organizationsDictionary)
            return [];
        return [
            ...this.vuexModule.organizationsDictionary.manager,
            ...this.vuexModule.organizationsDictionary.condifesa,
        ];
    }

    /**
     * GETTER
     * Intermediario/ Comitato di Gestione del Fondo
     */
    public get pcertIntermediary(){
        if(!this.precertificateData?.manager_org || !this.vuexModule.organizationsDictionary)
            return "";
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const index = this.managerSubjects.findIndex(({ cod_ente }) => cod_ente === this.precertificateData!.manager_org.toString())
        return this.managerSubjects?.[index]?.des_ente;
    }

    /**
     * GETTER
     * CONDIFESA / Soggetto Gestore
     */
    public get pcertCondifesa(){
        if(!this.precertificateData?.condifesa_org || !this.vuexModule.organizationsDictionary)
            return "";
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const index = this.managerSubjects.findIndex(({ cod_ente }) => cod_ente === this.precertificateData!.condifesa_org!.toString())
        return this.managerSubjects?.[index]?.des_ente;
    }
}
